import {
    Box,
    Button,
    Flex,
    Grid,
    IconButton,
    Table,
    TableContainer,
    Tbody,
    Td,
    Text,
    Th,
    Thead,
    Tooltip,
    Tr,
    useToast,
} from "@chakra-ui/react";
import { useCallback, useEffect, useState } from "react";
import { MdKeyboardDoubleArrowLeft } from "react-icons/md";
import { useHasPermission } from "../common/PermissionsFunction/permissiosnFunction";
import { HiOutlineRefresh } from "react-icons/hi";
import TableLoader from "components/common/TableLoader/TableLoader";
import { EditIcon } from "@chakra-ui/icons";
import Pagination from "components/common/Pagination/Pagination";
import CategoriesTable from "./CategoriesTable";
import { Paginationlimit } from "components/common/functions/variable";
import { appRequest } from "routes";
import CommonModal from "components/common/CommonModal/CommonModal";
import CreateFaq from "./CreateFaq/CreateFaq";
import debounce from "lodash.debounce";
import SearchBox from "components/layouts/MainWindow/Navbar/SearchBox";

type props = {
    categoryId: string;
};

export default function FaqTable({
    categoryId
}: props): JSX.Element {
    const [activePage, setActivePage] = useState("1");
    const [loading, setLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(0);
    const [faqs, setFaqs] = useState([]);
    const [searchQuery, setSearchQuery] = useState("");
    const [editFaqs, setEditFaqs] = useState({});
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const toast = useToast();

    const getFaqs = useCallback(
        async (currentP?: number) => {
            try {
                setLoading(true);
                let page = currentP ? currentP : currentPage;
                let limit = Paginationlimit;
                const request: any = {
                    limit,
                    page,
                    categoryId,
                    ...(searchQuery.trim() && { search: searchQuery.trim() }),
                    isActive: true,
                    populateCategory: true
                };

                // console.log("get all faq request", request);
                const response: any = await appRequest("faq", "getAll", request);

                if (response.status === "success") {
                    setFaqs(response?.data?.items || []);
                    setTotalPages(response?.data?.pages || 0);
                    // console.log("get all faq response", response);
                }
            } catch (error: any) {
                console.error("Error fetching faqs:", error);
            } finally {
                setLoading(false);
            }
        },
        [currentPage, searchQuery, categoryId] // Dependencies
    );

    const handleEdit = (item: any) => {
        setEditFaqs(item);
        setIsCreateModalOpen(true);
    };

    const updateFaq = async (editedData: any) => {
        // console.log("editedData in updatefaq", editedData)
        try {
            setLoading(true);
            const payload = {
                id: editedData._id,
                question: editedData?.question,
                answer: editedData?.answer,
                categoryId: editedData?.categoryId,
                order: editedData?.order,
                isActive: editedData?.isActive
            };
            // console.log("faqCategory update payload", payload)
            const result: any = await appRequest("faq", "update", payload);
            if (result.status === "success") {
                toast({
                    title: "Success",
                    description: "Category updated successfully!",
                    status: "success",
                    duration: 5000,
                    isClosable: true,
                    position: "top-right",
                });
                getFaqs();
                setIsCreateModalOpen(false);
            }
        } catch (error: any) {
            console.error("Error in updateing Category", error);
            toast({
                title: "Error",
                description: "Failed to update category.",
                status: "error",
                duration: 5000,
                isClosable: true,
                position: "top-right",
            });
        } finally {
            setLoading(false);
        }
    };

    const handlePageChange = (page: any) => {
        setCurrentPage(page.selected + 1);
    };

    const handleSearch = debounce((query: string) => {
        setSearchQuery(query);
        setCurrentPage(1); // Reset to the first page when searching
    }, 300); // Debounce for 300ms

    const handleRefreshAllData = () => {
        setSearchQuery("");
        getFaqs();
    };;

    useEffect(() => {
        getFaqs();
    }, [currentPage, searchQuery]);


    const canEditFaqs = useHasPermission("canEditFaqs");

    const handleBackButton = () => {
        setActivePage("2");
    };

    return (
        <>
            {activePage === "1" && (
                <Box
                    p={5}
                    rounded={"lg"}
                    bg={"white"}
                    shadow={"rgb(0 0 0 / 20%) 0px 0px 11px"}
                    h={"100%"}
                >
                    <Flex
                        direction={{ base: "column", md: "row" }}
                        justify="space-between"
                        gap={2}
                        mb={2}
                    >
                        {/* Back Button */}
                        <Tooltip label="Back to Cities" hasArrow>
                            <Button
                                onClick={handleBackButton}
                                colorScheme="teal"
                                aria-label="back to cities"
                                leftIcon={<MdKeyboardDoubleArrowLeft />}
                                size={{ base: "sm", md: "md" }}
                            >
                                Back
                            </Button>
                        </Tooltip>

                        {/* Search + Buttons */}
                        <Flex
                            direction={{ base: "column", md: "row" }}
                            gap={2}
                            align={{ base: "stretch", md: "center" }}
                        >
                            {/* Search Box */}
                            <SearchBox onSearch={handleSearch} />

                            {/* ADD + Refresh Buttons */}
                            <Grid
                                templateColumns={{ base: "1fr 1fr", md: "auto auto" }}
                                gap={2}
                                alignItems="center"
                            >
                                <Button
                                    onClick={() => {
                                        setIsCreateModalOpen(true);
                                        setEditFaqs({});
                                    }}
                                    colorScheme="teal"
                                    size={{ base: "sm", md: "md" }}
                                >
                                    ADD
                                </Button>

                                <Tooltip label="Refresh" hasArrow>
                                    <IconButton
                                        aria-label="Refresh"
                                        icon={<HiOutlineRefresh />}
                                        colorScheme="teal"
                                        variant="outline"
                                        borderWidth={2}
                                        _hover={{ bg: "teal.100" }}
                                        onClick={handleRefreshAllData}
                                        size={{ base: "sm", md: "md" }}
                                    />
                                </Tooltip>
                            </Grid>
                        </Flex>
                    </Flex>

                    <TableContainer className="customScrollBar" py={2}>
                        <Table variant="striped">
                            <Thead>
                                <Tr>
                                    <Th>Question</Th>
                                    <Th>Answer</Th>
                                    <Th>Category</Th>
                                    <Th>Order</Th>
                                    <Th>Status</Th>
                                    <Th>Actions</Th>
                                </Tr>
                            </Thead>

                            <TableLoader
                                noDataText="No FAQs Available"
                                show={faqs?.length}
                                loader={loading}
                            >
                                <Tbody>
                                    {faqs?.map((item: any, index: number) => (
                                        <Tr key={item._id}>
                                            <Td maxW="250px">
                                                <Text isTruncated noOfLines={2} wordBreak="break-word" whiteSpace="normal">
                                                    {item?.question || "N/A"}
                                                </Text>
                                            </Td>

                                            <Td maxW="350px">
                                                <Text isTruncated noOfLines={2} wordBreak="break-word" whiteSpace="normal">
                                                    {item?.answer || "N/A"}
                                                </Text>
                                            </Td>


                                            {/* Category */}
                                            <Td>
                                                <Text>{item?.category?.name || item?.category || "N/A"}</Text>
                                            </Td>

                                            {/* Order */}
                                            <Td>
                                                <Text>{item?.order ?? "N/A"}</Text>
                                            </Td>

                                            {/* Status */}
                                            <Td>
                                                <Text color={item?.isActive ? "green.500" : "red.500"}>
                                                    {item?.isActive ? "Active" : "Inactive"}
                                                </Text>
                                            </Td>

                                            {/* Actions */}
                                            <Td>
                                                <Flex gap="2">
                                                    {canEditFaqs && (
                                                        <IconButton
                                                            aria-label="Edit"
                                                            variant="outline"
                                                            colorScheme="teal"
                                                            size="sm"
                                                            icon={<EditIcon />}
                                                            onClick={() => handleEdit(item)}
                                                        />
                                                    )}
                                                    {/* <IconButton
                                                        aria-label="Delete"
                                                        variant="outline"
                                                        colorScheme="red"
                                                        size="sm"
                                                        icon={<FaTimes />}
                                                        onClick={() => handleDelete(item._id)}
                                                    /> */}
                                                </Flex>
                                            </Td>
                                        </Tr>
                                    ))}
                                </Tbody>
                            </TableLoader>
                        </Table>

                    </TableContainer>
                    <Pagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        handlePageChange={handlePageChange}
                    />
                    <CommonModal
                        header="Add/Update FAQs"
                        width={{ base: "100vw", md: "60vw" }}
                        animate={true}
                        isOpen={isCreateModalOpen}
                        onClose={() => setIsCreateModalOpen(false)}
                        body={
                            <Box>
                                <CreateFaq
                                    getFaqs={getFaqs}
                                    setIsCreateModalOpen={setIsCreateModalOpen}
                                    editData={editFaqs}
                                    onSubmit={updateFaq}
                                />
                            </Box>
                        }
                    />
                </Box>
            )}
            {activePage === "2" && <CategoriesTable />}
        </>
    );
}
